import axios from 'axios';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import nookies from 'nookies';
import { useCallback, useEffect, useState } from 'react';

import { BackButton, CountButton } from '../../components/buttons';
import Cart from '../../components/cart';
import CustomItem from '../../components/customItem';
import FadeImage from '../../components/fadeImage';
import { Badge, Divider } from '../../components/general';
import { AddItem, MenuItem } from '../../components/items';
import ExistingCart from '../../components/modals/existingCart';
import Navbar from '../../components/navbar';
import Page, { Content } from '../../components/page';
import Item from '../../components/popularItem';
import Settings from '../../components/settings';
import Subnav from '../../components/Subnav';
import { AUTH_TOKEN_KEY, useAuth } from '../../lib/auth';
import { API_URL } from '../../lib/constants';
import { addItem, addScenario, checkoutRouteForDelivery, createDelivery, createDeliveryItem } from '../../lib/delivery';
import { getPopularItems, getRelationships } from '../../lib/relationships';
import { BadgeColor, DeliveryItem, DeliveryStatus, Relationship, User } from '../../lib/types';
import { itemCount } from '../../lib/utils';

interface MenuProps {
  relationship: Relationship;
  popularItems: DeliveryItem[];
  user?: User;
}

const Menu: React.FC<MenuProps> = ({ relationship, popularItems, user }) => {
  const router = useRouter();
  const { delivery, deliveryType, updateDelivery, updateUser, updateType } = useAuth();

  const [showingModalCart, setShowingModalCart] = useState(false);
  const [showingSidebarCart, setShowingSidebarCart] = useState(false);

  const [showingSettings, setShowingSettings] = useState(false);
  const [showingCustomItem, setShowingCustomItem] = useState(false);
  const [currentItem, setCurrentItem] = useState<DeliveryItem | undefined>();

  const [showingExistingCart, setShowingExistingCart] = useState(false);
  const [pendingItem, setPendingItem] = useState<DeliveryItem | undefined>();

  useEffect(() => {
    if (user) {
      updateUser(user);
    }
  }, [user, updateUser]);

  const onAddToCart = useCallback(
    (item: DeliveryItem) => {
      const userId = user ? user.id : undefined;

      if (delivery) {
        // If we have a delivery, add the item if able.
        if (delivery.status !== DeliveryStatus.draft) {
          // Override.
          let newDelivery = createDelivery(userId, deliveryType);

          newDelivery = addScenario(newDelivery, relationship);
          newDelivery = addItem(newDelivery, item);

          updateDelivery(newDelivery);
          setCurrentItem(undefined);
          setShowingCustomItem(false);
          return;
        }

        if (delivery.relationshipId !== relationship.id) {
          // We bail and show an alert here.
          setPendingItem(item);
          setShowingExistingCart(true);
          return;
        }

        // Just add the item.
        updateDelivery(addItem(delivery, item));
        setCurrentItem(undefined);
        setShowingCustomItem(false);
        return;
      }

      // We don't have a current delivery, so create one.
      let newDelivery = createDelivery(userId, deliveryType);

      newDelivery = addScenario(newDelivery, relationship);
      newDelivery = addItem(newDelivery, item);

      updateDelivery(newDelivery);
      setCurrentItem(undefined);
      setShowingCustomItem(false);
    },
    [delivery, deliveryType, relationship, updateDelivery, user]
  );

  const onAddCustomItemToCart = useCallback(
    (name: string, notes: string, quantity: number) => {
      const item = createDeliveryItem(name, notes, quantity);
      onAddToCart(item);
    },
    [onAddToCart]
  );

  const onAddPopularItemToCart = useCallback(
    (item: DeliveryItem, notes: string, quantity: number) => {
      const newItem = createDeliveryItem(item.name, notes, quantity);
      onAddToCart(newItem);
    },
    [onAddToCart]
  );

  const onKeepExistingOrder = useCallback(() => {
    // Remove the pending item.
    setPendingItem(undefined);
    setShowingExistingCart(false);
  }, []);

  const onDiscardExistingOrder = useCallback(() => {
    // Create a new delivery and add the pending item.
    const userId = user ? user.id : undefined;
    let newDelivery = createDelivery(userId, deliveryType);

    newDelivery = addScenario(newDelivery, relationship);
    if (pendingItem) {
      newDelivery = addItem(newDelivery, pendingItem);
    }

    updateDelivery(newDelivery);
    setCurrentItem(undefined);
    setPendingItem(undefined);
    setShowingCustomItem(false);
    setShowingExistingCart(false);
  }, [deliveryType, pendingItem, relationship, updateDelivery, user]);

  const onCheckout = useCallback(() => {
    // Check to see if we have a user and ex before proceeding to confirm.
    if (!delivery) {
      console.error('No delivery when attempting to check out');
      return;
    }

    router.push(checkoutRouteForDelivery(delivery));
  }, [delivery, router]);

  const items = delivery && delivery.status === DeliveryStatus.draft ? delivery.items || [] : [];
  const count = itemCount(items);

  const onHideCart = useCallback(() => {
    setShowingModalCart(false);
    setShowingSidebarCart(false);
  }, []);

  const onShowSettings = useCallback(() => setShowingSettings(true), []);
  const onHideSettings = useCallback(() => setShowingSettings(false), []);

  const onShowCustomItem = useCallback(() => setShowingCustomItem(true), []);
  const onDismissItem = useCallback(() => {
    setCurrentItem(undefined);
    setShowingCustomItem(false);
  }, []);

  const onShowModalCart = useCallback(() => count > 0 && setShowingModalCart(true), [count]);
  const onShowSidebarCart = useCallback(() => count > 0 && setShowingSidebarCart(true), [count]);

  return (
    <Page className="bg-gray-100 lg:bg-white">
      <Navbar
        cartCount={count}
        className="hidden lg:flex"
        lightDesktop
        onAction={onShowSettings}
        onShowCart={onShowSidebarCart}
      />

      <FadeImage
        asBackground
        className="flex flex-col items-center justify-center bg-cover bg-center bg-no-repeat h-100 lg:h-72 blur"
        src={relationship.coverImageUrl}
      >
        <BackButton light className="absolute top-3 left-2 lg:hidden" title="" onClick={() => router.push('/feed')} />
        <h1 className="font-bold text-3xl text-white text-center px-4 lg:hidden">{relationship.name}</h1>
        <p className="bg-opacity-100 font-medium opacity-90 text-sm bg-purple-300 text-white mt-3 px-4 py-1.5 rounded-full lg:hidden">
          Delivery starting at $30
        </p>
      </FadeImage>

      <Subnav className="hidden lg:flex z-10" deliveryType={deliveryType} onTypeChange={updateType} />
      <Content>
        <div className="hidden lg:flex flex-col mx-4 mt-12">
          <div className="flex justify-between">
            <p className="text-xs font-medium uppercase text-pink-400 tracking-wide">Delivery starting at $30</p>
            {relationship.badgeType && (
              <Badge
                color={relationship.badgeColor || BadgeColor.pink}
                type={relationship.badgeType}
                className="ml-auto"
              />
            )}
          </div>
          <p className="mt-2 text-5xl font-bold">{relationship.name}</p>
          <p className="mt-2 text-base text-gray-400">{relationship.longDescription}</p>
        </div>
        <div className="mt-5 lg:mt-18 mx-4">
          <p className="font-medium text-base lg:text-2xl">Add a Custom Item</p>
          <Divider className="mt-4 mb-5 lg:mb-8" />
          <div className="lg:grid grid-cols-2 gap-x-8">
            <AddItem text="Custom Item" description="Tap to add description." onClick={onShowCustomItem} />
          </div>
        </div>

        {popularItems.length > 0 ? (
          <div className="mt-7 lg:mt-12 mx-4">
            <p className="font-medium text-base lg:text-2xl">Browse Suggested Items</p>
            <Divider className="mt-4 mb-5 lg:mb-8" />

            <div className={`flex flex-col lg:grid grid-cols-2 gap-x-8 ${items.length > 0 ? 'mb-24' : 'mb-4'}`}>
              {popularItems.map((item, idx) => (
                <MenuItem
                  key={item.id}
                  className="mb-4 lg:mb-8"
                  item={item}
                  badge={idx === 0}
                  onClick={setCurrentItem}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className={`${items.length > 0 ? 'mb-24' : 'mb-4'}`} />
        )}
      </Content>

      {count > 0 ? (
        <CountButton
          count={count}
          title="View Cart"
          className="uppercase fixed left-6 bottom-5 right-6 w-floating lg:hidden"
          onClick={onShowModalCart}
        />
      ) : null}

      <Item showing={!!currentItem} item={currentItem} onAdd={onAddPopularItemToCart} onDismiss={onDismissItem} />
      <CustomItem showing={showingCustomItem} onAdd={onAddCustomItemToCart} onDismiss={onDismissItem} />
      <ExistingCart
        delivery={delivery}
        relationship={relationship.name}
        showing={showingExistingCart}
        onDismiss={onKeepExistingOrder}
        onStartNew={onDiscardExistingOrder}
      />
      <Cart showing={showingModalCart} onCheckout={onCheckout} type="popup" onDismiss={onHideCart} />
      <Cart showing={showingSidebarCart} onCheckout={onCheckout} type="sidebar" onDismiss={onHideCart} />
      <Settings showing={showingSettings} onDismiss={onHideSettings} />
    </Page>
  );
};

export default Menu;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  try {
    let relationshipSlug = ctx.params.id;

    if (!relationshipSlug) {
      // No relationship, redirect to feed. (Shouldn't happen.)
      return {
        redirect: {
          permanent: false,
          destination: '/feed',
        },
      };
    }

    if (Array.isArray(relationshipSlug)) {
      // Next can receive this as an array, so grab first.
      relationshipSlug = relationshipSlug[0];
    }

    let user: User | undefined = undefined;

    const cookies = nookies.get(ctx);
    const token = cookies[AUTH_TOKEN_KEY];

    if (token) {
      // Attempt to fetch user.
      const headers = { Authorization: `Bearer ${token}` };
      const { data } = await axios.get<{ user?: User }>(`${API_URL}/user`, { headers });
      if (data.user) {
        user = data.user;
      } else {
        nookies.destroy(ctx, AUTH_TOKEN_KEY, { path: '/' });
      }
    }

    const relationships = getRelationships();
    const relationship = relationships.find((r) => r.id === relationshipSlug);
    if (!relationship) {
      // Not a valid relationship slug, redirect to feed.
      return {
        redirect: {
          permanent: false,
          destination: '/feed',
        },
      };
    }

    // Got the relationship, return as props.
    const props: MenuProps = {
      relationship,
      popularItems: getPopularItems(relationshipSlug),
    };

    if (user) {
      props.user = user;
    }

    return { props };
  } catch (err) {
    console.error('Error loading relationship', err.message);
    // Redirect to login page.
    return {
      redirect: {
        permanent: false,
        destination: '/',
      },
    };
  }
};
